import { useTranslation } from 'react-i18next';
import { ChatContextType, StorySummaryData, StorySummaryAIResponse } from '@packages/global';
import BaseStepChatPanel, { ActionCard } from './BaseStepChatPanel';

export interface StorySummaryChatPanelProps {
  projectId: string;
  episodeScriptId: string;
  currentData?: StorySummaryData | null;
  onApply?: (data: Partial<StorySummaryData>) => void;
}

export default function StorySummaryChatPanel({ projectId, episodeScriptId, currentData, onApply }: StorySummaryChatPanelProps) {
  const { t } = useTranslation('script');
  const parseResponse = (content: string): { message: string; actions: ActionCard[] } => {
    try {
      // 尝试多种方式解析 JSON
      let parsed: StorySummaryAIResponse | null = null;

      // 方式1: 匹配 ```json ... ``` 代码块
      const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        try {
          parsed = JSON.parse(jsonMatch[1]);
        } catch (e) {
          console.warn('JSON 代码块解析失败:', e);
        }
      }

      // 方式2: 匹配 ``` ... ``` 代码块（无 json 标记）
      if (!parsed) {
        const codeBlockMatch = content.match(/```\s*([\s\S]*?)\s*```/);
        if (codeBlockMatch) {
          try {
            parsed = JSON.parse(codeBlockMatch[1]);
          } catch (e) {
            console.warn('代码块解析失败:', e);
          }
        }
      }

      // 方式3: 尝试直接解析整个内容为 JSON
      if (!parsed) {
        try {
          parsed = JSON.parse(content);
        } catch (e) {
          console.warn('直接 JSON 解析失败:', e);
        }
      }

      // 方式4: 尝试提取 { ... } 部分
      if (!parsed) {
        const jsonObjectMatch = content.match(/\{[\s\S]*"content"[\s\S]*\}/);
        if (jsonObjectMatch) {
          try {
            parsed = JSON.parse(jsonObjectMatch[0]);
          } catch (e) {
            console.warn('JSON 对象提取解析失败:', e);
          }
        }
      }

      // 如果成功解析，构建 actions
      if (parsed) {
        const actions: ActionCard[] = [];
        if (parsed.content) {
          const { title, summary, highlights } = parsed.content;
          const highlightNames = (highlights || []).map(h => h.name).join('、');
          actions.push({
            type: 'update',
            title: t('storySummary.title'),
            content: `${t('storySummary.storyTitle')}: ${title || ''}\n\n${t('storySummary.noSummary')}: ${(summary || '').substring(0, 150)}${(summary || '').length > 150 ? '...' : ''}\n\n${t('storySummary.highlights')}: ${highlightNames || ''}`,
            data: parsed.content
          });
        }
        return { message: parsed.message || '', actions };
      }

      // 解析失败，返回原始内容
      return { message: content, actions: [] };
    } catch (e) {
      console.error('parseResponse 异常:', e);
      return { message: content, actions: [] };
    }
  };

  const handleApplyAction = (action: ActionCard) => {
    if (action.data && onApply) {
      onApply(action.data as Partial<StorySummaryData>);
    }
  };

  return (
    <BaseStepChatPanel
      projectId={projectId}
      episodeScriptId={episodeScriptId}
      stepNumber={1}
      stepName={t('step.storySummary')}
      contextType={ChatContextType.SCRIPT_STORY_SUMMARY}
      currentStepData={currentData}
      onApplyAction={handleApplyAction}
      parseResponse={parseResponse}
    />
  );
}
